import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  /** HTTP status when the failure came from the API, so callers can tell a
   *  misconfigured deployment (503) from a real error. */
  status: number | null;
  loading: boolean;
  reload: () => void;
}

// Small loader used by every screen: one request, an error string instead of a
// thrown exception, and a reload for after a write.
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const run = useCallback(fn, deps);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setStatus(null);
    run()
      .then((d) => alive && setData(d))
      .catch((e: Error) => {
        if (!alive) return;
        setError(e.message);
        setStatus(e instanceof ApiError ? e.status : null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [run, tick]);

  return { data, error, status, loading, reload: () => setTick((t) => t + 1) };
}
